import React, { useState } from "react";

import { motion } from "framer-motion";

import {
  MapPin,
  Briefcase,
  Clock3,
  Building2,
  ArrowRight,
} from "lucide-react";

import JobApplicationModal from "../JobApplicationModal";

const JobBanner = ({ job }) => {
  const [openApply, setOpenApply] = useState(false);

  const infoCards = [
    {
      icon: <Building2 size={22} />,
      label: "Department",
      value: job.department,
    },
    {
      icon: <MapPin size={22} />,
      label: "Location",
      value: job.location,
    },
    {
      icon: <Clock3 size={22} />,
      label: "Experience",
      value: job.experience,
    },
    {
      icon: <Briefcase size={22} />,
      label: "Employment Type",
      value: job.type,
    },
  ];

  return (
    <>
      {/* ============================================================
          JOB BANNER
      ============================================================ */}
      <section
        className="
          relative
          overflow-hidden
          theme-bg-primary
          pt-24
          sm:pt-28
          md:pt-32
          lg:pt-36
          pb-6
          sm:pb-8
          md:pb-10
          font-saira
        "
      >
        {/* ============================================================
            BACKGROUND GLOW
        ============================================================ */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.12, 1],
              opacity: [0.12, 0.22, 0.12],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
            }}
            className="
              absolute
              left-1/2
              top-8
              h-[240px]
              w-[240px]
              -translate-x-1/2
              rounded-full
              blur-[90px]
              sm:top-10
              sm:h-[340px]
              sm:w-[340px]
              sm:blur-[120px]
              md:h-[420px]
              md:w-[420px]
              md:blur-[150px]
              lg:h-[520px]
              lg:w-[520px]
              lg:blur-[170px]
            "
            style={{
              backgroundColor: "var(--brand-primary)",
            }}
          />
        </div>

        {/* ============================================================
            CONTAINER
        ============================================================ */}
        <div
          className="
            relative
            mx-auto
            w-full
            max-w-7xl
            px-4
            sm:px-6
            lg:px-8
          "
        >
          <div
            className="
              grid
              items-center
              gap-8
              md:gap-10
              lg:grid-cols-12
              lg:gap-16
            "
          >
            {/* ========================================================
                LEFT CONTENT
            ======================================================== */}
            <div className="lg:col-span-7">
              {/* ======================================================
                  DEPARTMENT BADGE
              ====================================================== */}
              <motion.div
                initial={{ opacity: 0, y: 25 }}
                animate={{ opacity: 1, y: 0 }}
                className="
                  inline-flex
                  max-w-full
                  items-center
                  rounded-full
                  border
                  px-4
                  py-2
                  text-xs
                  font-semibold
                  uppercase
                  tracking-[0.18em]
                  sm:px-5
                  sm:text-sm
                "
                style={{
                  borderColor: "color-mix(in srgb, var(--brand-primary) 30%, transparent)",
                  backgroundColor:
                    "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
                  color: "var(--brand-light)",
                }}
              >
                <Building2 size={16} className="mr-2 shrink-0" />

                <span className="truncate">{job.department}</span>
              </motion.div>

              {/* ======================================================
                  TITLE
              ====================================================== */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="
                  mt-5
                  break-words
                  text-3xl
                  font-bold
                  leading-tight
                  theme-text-primary
                  sm:text-4xl
                  md:text-5xl
                  lg:text-6xl
                "
              >
                {job.title}
              </motion.h1>

              {/* ======================================================
                  DESCRIPTION
              ====================================================== */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="
                  mt-5
                  max-w-3xl
                  text-base
                  leading-8
                  theme-text-muted
                  sm:text-lg
                  sm:leading-9
                "
              >
                {job.description}
              </motion.p>

              {/* ======================================================
                  APPLY BUTTON
              ====================================================== */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                onClick={() => setOpenApply(true)}
                className="
                  group
                  mt-8
                  inline-flex
                  w-full
                  items-center
                  justify-center
                  gap-3
                  rounded-xl
                  px-8
                  py-4
                  text-base
                  font-semibold
                  text-white
                  transition-all
                  duration-300
                  hover:shadow-[0_18px_40px_var(--shadow-color)]
                  sm:w-auto
                  sm:text-lg
                "
                style={{
                  backgroundColor: "var(--brand-primary)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "var(--brand-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "var(--brand-primary)";
                }}
              >
                <span>Apply Now</span>

                <ArrowRight
                  size={20}
                  className="
                    transition-transform
                    duration-300
                    group-hover:translate-x-1
                  "
                />
              </motion.button>
            </div>

            {/* ========================================================
                RIGHT INFORMATION CARDS
            ======================================================== */}
            <div className="lg:col-span-5">
              <div
                className="
                  grid
                  grid-cols-2
                  gap-3
                  sm:gap-4
                  lg:gap-5
                "
              >
                {infoCards.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{
                      opacity: 0,
                      y: 25,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      delay: 0.15 * index,
                    }}
                    whileHover={{
                      y: -5,
                    }}
                    className="
                      theme-card
                      rounded-2xl
                      border
                      theme-border
                      p-4
                      transition-all
                      duration-300
                      sm:p-5
                      lg:p-6
                    "
                    style={{
                      boxShadow: "0 8px 24px var(--shadow-color)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "color-mix(in srgb, var(--brand-primary) 40%, transparent)";

                      e.currentTarget.style.backgroundColor =
                        "var(--bg-tertiary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--border-primary)";

                      e.currentTarget.style.backgroundColor =
                        "var(--card-bg)";
                    }}
                  >
                    {/* ==================================================
                        ICON
                    ================================================== */}
                    <div
                      className="
                        mb-4
                        flex
                        h-11
                        w-11
                        items-center
                        justify-center
                        rounded-xl
                        sm:h-12
                        sm:w-12
                        lg:h-14
                        lg:w-14
                        lg:rounded-2xl
                      "
                      style={{
                        backgroundColor:
                          "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
                        color: "var(--brand-primary)",
                      }}
                    >
                      {item.icon}
                    </div>

                    {/* ==================================================
                        LABEL
                    ================================================== */}
                    <p
                      className="
                        text-[10px]
                        uppercase
                        tracking-[0.18em]
                        theme-text-muted
                        sm:text-xs
                      "
                    >
                      {item.label}
                    </p>

                    {/* ==================================================
                        VALUE
                    ================================================== */}
                    <h3
                      className="
                        mt-2
                        break-words
                        text-base
                        font-semibold
                        leading-7
                        theme-text-primary
                        sm:text-lg
                        lg:text-xl
                      "
                    >
                      {item.value}
                    </h3>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================================================
          JOB APPLICATION MODAL
      ============================================================== */}
      <JobApplicationModal
        isOpen={openApply}
        onClose={() => setOpenApply(false)}
        job={job}
      />
    </>
  );
};

export default JobBanner;